/*
 * BUILD YOUR OWN KIT — assets/kit-builder.js (2026-08 rebuild, guided-step flow)
 *
 * The cart is the state:
 *  - on load, on bfcache restore, on tab refocus, and whenever the theme announces a cart
 *    change (drawer edits included) we re-read /cart.js; lines carrying properties._kit
 *    are part of the kit
 *  - picking products only touches local state (sessionStorage-backed, survives refresh)
 *  - ADD MY KIT sends ONE atomic POST /cart/add.js with items[] — Shopify takes the whole
 *    kit or rejects it with a real error; partial carts are impossible by construction
 *  - after success we fire Impact's own `cart:refresh` and open the drawer, which shows
 *    the REAL discounted total; no client-side number is ever authoritative
 *
 * Presentation is a guided stepper: one category per step, a progress rail whose thumbs
 * read as an equation (section + section + section = kit), a review step with a totals
 * closer, and a global sticky bar. Swipe left/right moves between steps on touch.
 * No jQuery. Motion One (bundled sitewide in vendor.min.js) loads off the critical path;
 * every animation degrades to visible-and-static.
 */

const root = document.querySelector('[data-kit-builder]');

if (root) {
  const MIN = parseInt(root.dataset.minItems, 10) || 4;
  const PCT = parseInt(root.dataset.discountPercent, 10) || 25;
  const MAX_PER = parseInt(root.dataset.maxPerItem, 10) || 10;
  const KIT_PROP = '_kit';

  const MOTION_OK = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
  // animations are optional garnish: never gate interactivity on the vendor bundle
  let motion = null;
  if (MOTION_OK) {
    import('vendor').then((m) => { motion = m; }).catch(() => { /* static is fine */ });
  }

  /* ---------- money ---------- */
  const moneyFormat = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '${{amount}}';
  const money = (cents) => {
    const fixed = (cents / 100).toFixed(2);
    const [int, dec] = fixed.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, `${grouped}.${dec}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grouped);
  };

  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  // cart.js hands back full-size image URLs; ask the CDN for a thumb instead.
  // Card-sourced URLs already carry a width param - never stack a second one.
  const thumb = (url, w) => !url ? '' : (/[?&]width=/.test(url) ? url : url + (url.includes('?') ? '&' : '?') + 'width=' + w);
  const handleize = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  /* ---------- state ---------- */
  // pending: variantId -> { qty, title, variantTitle, price (cents), image, step }
  const pending = new Map();
  // inCart: variantId -> { qty, key (line item key), title, variantTitle, price, image }
  let inCart = new Map();
  let submitting = false;

  const PENDING_KEY = 'bossmanKitPending';
  const STEP_KEY = 'bossmanKitStep';
  const variantIndex = new Map();
  function persistPending() {
    try {
      const out = {};
      pending.forEach((p, id) => { out[id] = p.qty; });
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(out));
    } catch (e) { /* storage unavailable: picks just won't survive refresh */ }
  }
  function restorePending() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(PENDING_KEY) || '{}');
      for (const [id, qty] of Object.entries(saved)) {
        const info = variantIndex.get(Number(id));
        if (info && qty > 0) pending.set(Number(id), { ...info, qty: Math.min(qty, MAX_PER) });
      }
    } catch (e) { /* corrupt state: start clean */ }
  }

  const kitQtyFor = (id) => (pending.get(id) ? pending.get(id).qty : 0) + (inCart.get(id) ? inCart.get(id).qty : 0);
  const kitCount = () => {
    let n = 0;
    pending.forEach((p) => { n += p.qty; });
    inCart.forEach((c) => { n += c.qty; });
    return n;
  };
  const kitTotalCents = () => {
    let t = 0;
    pending.forEach((p) => { t += p.price * p.qty; });
    inCart.forEach((c) => { t += c.price * c.qty; });
    return t;
  };

  /* ---------- cart sync ---------- */
  async function readCart() {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const next = new Map();
      for (const item of cart.items || []) {
        if (item.properties && item.properties[KIT_PROP]) {
          next.set(item.variant_id, {
            qty: item.quantity,
            key: item.key,
            title: item.product_title,
            variantTitle: item.variant_title || '',
            price: item.price,
            image: item.image || ''
          });
        }
      }
      inCart = next;
      // a line already in the cart shrinks how much more of that variant can be picked
      pending.forEach((p, id) => {
        const room = MAX_PER - (inCart.get(id) ? inCart.get(id).qty : 0);
        if (p.qty > room) {
          if (room <= 0) pending.delete(id);
          else p.qty = room;
        }
      });
    } catch (e) { /* offline or throttled: keep last known state */ }
    render();
  }

  // the drawer is part of the same state machine: when the theme announces any cart
  // mutation (drawer quantity edits, removals, other tabs via refocus), re-read reality
  let cartSyncTimer = null;
  const scheduleCartSync = () => {
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(readCart, 350);
  };
  document.addEventListener('cart:change', scheduleCartSync);
  document.documentElement.addEventListener('cart:change', scheduleCartSync);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleCartSync();
  });

  function refreshDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function openDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      if (typeof drawer.show === 'function') drawer.show();
      else drawer.setAttribute('open', '');
    } else {
      window.location.href = '/cart';
    }
  }

  /* ---------- messaging surfaces ---------- */
  // review-step banner: for submit/cart failures, where review is the natural landing
  const errorEl = root.querySelector('[data-kit-error]');
  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
    if (currentStep !== 'review') goTo('review');
    errorEl.scrollIntoView({ block: 'nearest', behavior: MOTION_OK ? 'smooth' : 'auto' });
  }
  function clearError() {
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
  }
  // transient toast: for in-context notices that must NOT yank the shopper anywhere.
  // Optionally carries one action (the add-undo lives here).
  const toastEl = document.querySelector('[data-kit-toast]');
  const toastMsg = toastEl && toastEl.querySelector('[data-kit-toast-msg]');
  const toastAction = toastEl && toastEl.querySelector('[data-kit-toast-action]');
  let toastTimer = null;
  let toastActionFn = null;
  function showToast(msg, opts = {}) {
    if (!toastEl) return;
    if (toastMsg) toastMsg.textContent = msg;
    toastActionFn = opts.onAction || null;
    if (toastAction) {
      toastAction.hidden = !toastActionFn;
      if (toastActionFn) toastAction.textContent = opts.actionLabel || 'UNDO';
    }
    toastEl.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toastEl.hidden = true; }, opts.duration || 3500);
  }
  if (toastAction) {
    toastAction.addEventListener('click', () => {
      if (toastActionFn) toastActionFn();
      toastActionFn = null;
      toastEl.hidden = true;
    });
  }
  // screen-reader announcements for state that is otherwise conveyed visually
  const srEl = document.querySelector('[data-kit-sr]');
  function announce(msg) {
    if (!srEl) return;
    srEl.textContent = '';
    requestAnimationFrame(() => { srEl.textContent = msg; });
  }

  /* ---------- step engine ---------- */
  const steps = [...root.querySelectorAll('[data-kit-step]')];
  const stepIds = steps.map((s) => s.dataset.kitStep); // ['0','1','2','review']
  const railSteps = [...root.querySelectorAll('[data-kit-rail-step]')];
  let currentStep = '0';

  const stepHasCards = (id) => {
    const el = steps.find((s) => s.dataset.kitStep === id);
    return id === 'review' || (el && el.querySelector('[data-kit-card]'));
  };
  const navIds = stepIds.filter(stepHasCards);
  railSteps.forEach((btn) => {
    if (!stepHasCards(btn.dataset.kitRailStep)) btn.hidden = true;
  });

  function goTo(id, dir) {
    if (!navIds.includes(id) || id === currentStep) return;
    const from = steps.find((s) => s.dataset.kitStep === currentStep);
    const to = steps.find((s) => s.dataset.kitStep === id);
    if (!to) return;
    if (from) from.hidden = true;
    to.hidden = false;
    currentStep = id;
    try { sessionStorage.setItem(STEP_KEY, id); } catch (e) { /* fine */ }
    if (id === 'review') renderReview();
    syncRail();
    syncStepNav();
    syncContinue();
    const rail = root.querySelector('[data-kit-rail]');
    if (rail) {
      // scroll so the sticky rail lands exactly at its pinned position, not over the heading
      const stickyTop = parseFloat(getComputedStyle(rail).top) || 0;
      const y = rail.getBoundingClientRect().top + window.scrollY - stickyTop - 8;
      window.scrollTo({ top: Math.max(y, 0), behavior: MOTION_OK ? 'smooth' : 'auto' });
    }
    const title = to.querySelector('.kit-step__title');
    if (title) title.focus({ preventScroll: true });
    announce(stepLabel(id) + (id === 'review' ? '' : ' step'));
    if (motion) {
      const slide = dir === 'back' ? '-24px' : '24px';
      motion.animate(to, { opacity: [0, 1], transform: [`translateX(${slide})`, 'translateX(0)'] }, { duration: 0.3 });
      const cards = to.querySelectorAll('.kit-card');
      if (cards.length) {
        motion.animate(cards, { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 0.25, delay: motion.stagger(0.03) });
      }
    }
  }

  function stepQty(id) {
    let n = 0;
    pending.forEach((p) => { if (p.step === id) n += p.qty; });
    inCart.forEach((c, vid) => {
      const info = variantIndex.get(vid);
      if (info && info.step === id) n += c.qty;
    });
    return n;
  }
  // distinct picked lines in a step, in cart-then-pending order (one thumb per line)
  function stepImages(id) {
    const imgs = [];
    inCart.forEach((c, vid) => {
      const info = variantIndex.get(vid);
      if (info && info.step === id && info.image) imgs.push(info.image);
    });
    pending.forEach((p) => { if (p.step === id && p.image) imgs.push(p.image); });
    return imgs;
  }

  const MAX_RAIL_THUMBS = 3;
  // Rising-edge latch for the "Your Kit" celebration. Deliberately NOT the module's
  // `wasUnlocked` flag: render() assigns that (js ~:773) BEFORE it calls syncRail() (~:777),
  // so `unlocked && !wasUnlocked` is always false by the time we get here. It also lives
  // inside `if (bar)`, so it would never update at all if the sticky bar were absent.
  let railCelebrated = false;
  function syncRail() {
    railSteps.forEach((btn) => {
      const id = btn.dataset.kitRailStep;
      const isCurrent = id === currentStep;
      btn.classList.toggle('is-current', isCurrent);
      if (isCurrent) btn.setAttribute('aria-current', 'step');
      else btn.removeAttribute('aria-current');
      if (id === 'review') {
        const kitTotal = kitCount();
        btn.setAttribute('aria-label', `Your Kit, ${kitTotal} items picked`);
        const unlocked = kitTotal >= MIN;
        btn.classList.toggle('is-unlocked', unlocked);
        if (unlocked && !railCelebrated) {
          railCelebrated = true;
          btn.classList.add('just-unlocked');
          // fixed timeout, not animationend: two elements animate and the shorter one
          // would strip the class while the other is still running
          setTimeout(() => btn.classList.remove('just-unlocked'), 900);
        } else if (!unlocked) {
          railCelebrated = false;
          btn.classList.remove('just-unlocked');
        }
        return;
      }
      const slot = btn.querySelector('[data-kit-rail-slot]');
      const qty = stepQty(id);
      btn.classList.toggle('is-filled', qty > 0);
      btn.setAttribute('aria-label', `${stepLabel(id)}, ${qty} picked`);
      if (!slot) return;
      const imgs = stepImages(id);
      const sig = imgs.slice(0, MAX_RAIL_THUMBS).join('|') + ':' + imgs.length;
      if (slot.dataset.sig === sig) return;
      slot.dataset.sig = sig;
      if (!imgs.length) {
        slot.innerHTML = '<span class="kit-rail__circle kit-rail__circle--empty"><span class="kit-rail__num" aria-hidden="true">+</span></span>';
        return;
      }
      // bare thumbs sitting together; pluses live BETWEEN sections, not between items
      const parts = imgs.slice(0, MAX_RAIL_THUMBS).map((img) =>
        `<img class="kit-rail__thumb" src="${esc(img)}" alt="" width="40" height="40" loading="lazy">`);
      if (imgs.length > MAX_RAIL_THUMBS) {
        parts.push(`<span class="kit-rail__more" aria-hidden="true">+${imgs.length - MAX_RAIL_THUMBS}</span>`);
      }
      slot.innerHTML = parts.join('');
    });
  }

  function syncStepNav() {
    const el = steps.find((s) => s.dataset.kitStep === currentStep);
    if (!el || currentStep === 'review') return;
    const next = el.querySelector('[data-kit-next]');
    if (!next) return;
    const idx = navIds.indexOf(currentStep);
    const isLastProductStep = navIds[idx + 1] === 'review';
    const picked = stepQty(currentStep) > 0;
    next.textContent = isLastProductStep ? 'REVIEW MY KIT' : (picked ? 'NEXT' : 'SKIP FOR NOW');
    next.classList.toggle('button--subdued', !picked && !isLastProductStep);
  }

  /* ---------- swipe between steps (touch) ---------- */
  {
    let t0 = null;
    root.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1 || e.target.closest('[data-kit-rail]')) { t0 = null; return; }
      t0 = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
    }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (!t0) return;
      const dx = e.changedTouches[0].clientX - t0.x;
      const dy = e.changedTouches[0].clientY - t0.y;
      const dt = Date.now() - t0.t;
      t0 = null;
      if (dt > 600) return;
      if (Math.abs(dx) < 60) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const idx = navIds.indexOf(currentStep);
      if (dx < 0 && idx < navIds.length - 1) goTo(navIds[idx + 1]);
      else if (dx > 0 && idx > 0) goTo(navIds[idx - 1], 'back');
    }, { passive: true });
  }

  root.querySelectorAll('[data-kit-next]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = navIds.indexOf(currentStep);
      if (idx < navIds.length - 1) goTo(navIds[idx + 1]);
    });
  });
  root.querySelectorAll('[data-kit-back]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = navIds.indexOf(currentStep);
      if (idx > 0) goTo(navIds[idx - 1], 'back');
    });
  });
  railSteps.forEach((btn) => {
    btn.addEventListener('click', () => goTo(btn.dataset.kitRailStep));
  });

  /* ---------- product cards ---------- */
  // Repaint a card's hero image to match the picked variant.
  // THE TRAP: setting img.src alone changes nothing visible, because the Liquid image_tag
  // emits a srcset ('200,300,400,600' widths) and srcset always wins. Clear srcset/sizes
  // and let src govern. width/height are left untouched so the aspect-ratio:1 media box
  // never shifts. Gated on "this variant has an image", not on the option being named
  // Scent - the Soap Dish picker is Color and should still swap.
  function setCardImage(card, variant) {
    const img = card.querySelector('.kit-card__img');
    if (!img) return;
    if (!img.dataset.fallbackSrc) img.dataset.fallbackSrc = img.getAttribute('src') || '';
    const next = (variant && variant.image) || img.dataset.fallbackSrc;
    if (!next || img.getAttribute('src') === next) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = next;
  }

  // card element -> its variant ids, for picked-state rendering
  const cardIndex = [];
  root.querySelectorAll('[data-kit-card]').forEach((card) => {
    let data;
    try {
      data = JSON.parse(card.querySelector('[data-kit-variants]').textContent);
    } catch (e) { return; }
    const variants = data.variants || [];
    if (!variants.length) return;

    const stepEl = card.closest('[data-kit-step]');
    const stepId = stepEl ? stepEl.dataset.kitStep : '0';
    const optNames = data.optNames || [];
    const priceEl = card.querySelector('[data-kit-price]');
    const optionsEl = card.querySelector('[data-kit-options]');
    const addBtn = card.querySelector('[data-kit-add]');
    // data-kit-default is the variant that owns the product's featured image, so the
    // pressed pill matches the photo the card renders. Falls back to the first kept
    // variant when no variant owns the hero shot.
    const defaultId = card.dataset.kitDefault;
    let selected = variants.find((v) => String(v.id) === defaultId) || variants[0];
    // no-op on load now that the Liquid renders the same variant; self-corrects on drift
    setCardImage(card, selected);

    for (const v of variants) {
      variantIndex.set(v.id, {
        title: card.dataset.productTitle,
        variantTitle: v.title === 'Default Title' ? '' : v.title,
        price: v.price,
        // per-variant thumb, so the progress rail and the review chips show the scent
        // that was actually picked rather than the card's default one
        image: v.thumb || card.dataset.productImage || '',
        step: stepId
      });
    }

    // on-card picked badge: without it, unsure shoppers tap ADD again and stack quantity
    const badge = document.createElement('span');
    badge.className = 'kit-card__picked';
    badge.hidden = true;
    card.appendChild(badge);

    // once picked, ADD hands over to a stepper: remove exactly where you added
    const stepper = document.createElement('div');
    stepper.className = 'kit-card__stepper';
    stepper.hidden = true;
    stepper.innerHTML =
      `<button type="button" class="kit-card__stepper-btn" aria-label="Remove one ${esc(card.dataset.productTitle)}">&minus;</button>` +
      `<span class="kit-card__stepper-count">0</span>` +
      `<button type="button" class="kit-card__stepper-btn" aria-label="Add one more ${esc(card.dataset.productTitle)}">&plus;</button>`;
    addBtn.insertAdjacentElement('afterend', stepper);
    const stepperCount = stepper.querySelector('.kit-card__stepper-count');
    const [minusBtn, , plusBtn] = stepper.children;
    plusBtn.addEventListener('click', () => addBtn.click());
    minusBtn.addEventListener('click', () => {
      const ids = variants.map((v) => v.id);
      // pending picks go first (instant, local) - prefer the selected variant
      const pendingIds = ids.filter((vid) => pending.get(vid));
      const target = pendingIds.includes(selected.id) ? selected.id : pendingIds[pendingIds.length - 1];
      if (target) {
        const p = pending.get(target);
        p.qty -= 1;
        if (p.qty <= 0) pending.delete(target);
        announce(`Removed ${variantIndex.get(target).title}. ${kitCount()} of ${MIN} picked.`);
        render();
        return;
      }
      // nothing pending: this card's picks are already in the cart
      const cartIds = ids.filter((vid) => inCart.get(vid));
      const ct = cartIds.includes(selected.id) ? selected.id : cartIds[cartIds.length - 1];
      if (ct) adjust(ct, 'cart', -1);
    });

    cardIndex.push({ card, badge, ids: variants.map((v) => v.id), stepper, stepperCount, addBtn });

    const varying = [];
    optNames.forEach((name, idx) => {
      const values = [...new Set(variants.map((v) => v.options[idx]))];
      if (values.length > 1) varying.push({ idx, name, values });
    });

    function resolve(sel, clickedIdx) {
      let match = variants.find((v) => varying.every((o) => v.options[o.idx] === sel[o.idx]));
      // no exact combo: honor the option the shopper JUST touched, not always option 1
      if (!match && clickedIdx != null) {
        match = variants.find((v) => v.options[clickedIdx] === sel[clickedIdx]);
      }
      if (!match && varying.length) {
        match = variants.find((v) => v.options[varying[0].idx] === sel[varying[0].idx]);
      }
      return match || null;
    }

    const sel = {};
    varying.forEach((o) => { sel[o.idx] = selected.options[o.idx]; });

    varying.forEach((o) => {
      const isScent = /scent/i.test(o.name);
      const row = document.createElement('div');
      row.className = 'kit-card__optrow';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', `${o.name.replace(/[:\s]+$/, '')} for ${card.dataset.productTitle}`);
      const label = document.createElement('span');
      label.className = 'kit-card__optname';
      label.textContent = o.name.replace(/[:\s]+$/, '');
      row.appendChild(label);
      const pills = document.createElement('div');
      pills.className = 'kit-card__pills';
      o.values.forEach((value) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'kit-pill';
        if (isScent) {
          const dot = document.createElement('span');
          dot.className = 'kit-pill__dot';
          pill.appendChild(dot);
          pill.appendChild(document.createTextNode(value));
          pill.dataset.swatch = handleize(value);
        } else {
          pill.textContent = value;
        }
        pill.setAttribute('aria-pressed', String(value === sel[o.idx]));
        pill.addEventListener('click', () => {
          sel[o.idx] = value;
          const match = resolve(sel, o.idx);
          if (!match) return;
          selected = match;
          varying.forEach((oo) => { sel[oo.idx] = match.options[oo.idx]; });
          optionsEl.querySelectorAll('.kit-card__optrow').forEach((r, ri) => {
            const o2 = varying[ri];
            r.querySelectorAll('.kit-pill').forEach((p) => {
              p.setAttribute('aria-pressed', String(p.textContent === sel[o2.idx]));
            });
          });
          if (priceEl) priceEl.textContent = money(selected.price);
          setCardImage(card, selected);
        });
        pills.appendChild(pill);
      });
      row.appendChild(pills);
      optionsEl.appendChild(row);
    });

    if (priceEl) priceEl.textContent = money(selected.price);
    addBtn.disabled = false;

    addBtn.addEventListener('click', () => {
      const id = selected.id;
      if (kitQtyFor(id) >= MAX_PER) {
        showToast(`Max ${MAX_PER} per product. Your kit already has ${kitQtyFor(id)} of this one.`);
        return;
      }
      const existing = pending.get(id);
      if (existing) existing.qty += 1;
      else pending.set(id, { ...variantIndex.get(id), qty: 1 });
      if (motion) {
        motion.animate(addBtn, { transform: ['scale(1)', 'scale(0.94)', 'scale(1)'] }, { duration: 0.25 });
        const idx = navIds.indexOf(currentStep);
        const nextChip = railSteps.find((b) => b.dataset.kitRailStep === navIds[idx + 1]);
        if (nextChip) {
          motion.animate(nextChip, { transform: ['scale(1)', 'scale(1.12)', 'scale(1)'] }, { duration: 0.5 });
        }
      }
      addBtn.classList.add('is-added');
      setTimeout(() => addBtn.classList.remove('is-added'), 900);
      const count = kitCount();
      const info = variantIndex.get(id);
      announce(`Added ${info.title}. ${count} of ${MIN} picked.` + (count >= MIN ? ` ${PCT}% off unlocked.` : ''));
      // reflexive-regret escape hatch: undo the exact variant just added
      showToast(`Added ${info.title}`, {
        onAction: () => {
          const p = pending.get(id);
          if (!p) return;
          p.qty -= 1;
          if (p.qty <= 0) pending.delete(id);
          announce(`Removed ${info.title}. ${kitCount()} of ${MIN} picked.`);
          render();
        }
      });
      render();
    });
  });

  /* ---------- render targets ---------- */
  // NOTE: the sticky bar lives OUTSIDE [data-kit-builder]; query bar internals from document
  const countEl = document.querySelector('[data-kit-count]');
  const listEl = root.querySelector('[data-kit-list]');
  const emptyEl = root.querySelector('[data-kit-empty]');
  const reviewHint = root.querySelector('[data-kit-review-hint]');
  const reviewTotals = root.querySelector('[data-kit-review-totals]');
  const bar = document.querySelector('[data-kit-bar]');
  const barZero = bar && bar.querySelector('[data-kit-bar-zero]');
  const barLocked = bar && bar.querySelector('[data-kit-bar-locked]');
  const barUnlocked = bar && bar.querySelector('[data-kit-bar-unlocked]');
  const barRemaining = bar && bar.querySelector('[data-kit-remaining]');
  const barPrice = bar && bar.querySelector('[data-kit-bar-price]');
  const barFull = bar && bar.querySelector('[data-kit-total-full]');
  const barDisc = bar && bar.querySelector('[data-kit-total-disc]');
  const submitBtn = bar && bar.querySelector('[data-kit-submit]');
  const continueBtn = bar && bar.querySelector('[data-kit-continue]');
  const reviewSubmit = root.querySelector('[data-kit-review-submit]');
  let wasUnlocked = false;
  let flashUntil = 0;

  function stepLabel(id) {
    if (id === 'review') return 'Your Kit';
    const chip = railSteps.find((b) => b.dataset.kitRailStep === id);
    const l = chip && chip.querySelector('.kit-rail__label');
    return l ? l.textContent.trim() : '';
  }
  function syncContinue() {
    if (!continueBtn) return;
    const idx = navIds.indexOf(currentStep);
    const next = navIds[idx + 1];
    const show = next && currentStep !== 'review' && stepQty(currentStep) > 0;
    continueBtn.hidden = !show;
    if (show) continueBtn.textContent = next === 'review' ? 'REVIEW MY KIT ›' : `NEXT: ${stepLabel(next).toUpperCase()} ›`;
  }
  if (continueBtn) {
    continueBtn.addEventListener('click', () => {
      const idx = navIds.indexOf(currentStep);
      if (idx < navIds.length - 1) goTo(navIds[idx + 1]);
    });
  }

  let heroPassed = false, footerSeen = false;
  function syncBar() {
    if (!bar) return;
    bar.classList.toggle('is-visible', kitCount() > 0 || (heroPassed && !footerSeen));
  }
  // the bar overlays page bottom on phones; reserve real estate so BACK/NEXT and the
  // guarantee never hide behind it. Set once directly (observer delivery is not
  // guaranteed in every rendering context), then track size changes.
  if (bar) {
    const setBarHeight = () => root.style.setProperty('--kit-bar-height', bar.offsetHeight + 'px');
    setBarHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setBarHeight).observe(bar);
  }
  {
    const heroEl = root.querySelector('.kit-hero');
    const footerEl = document.querySelector('.footer');
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === heroEl) heroPassed = e.boundingClientRect.bottom < 0;
        if (e.target === footerEl) footerSeen = e.isIntersecting;
      }
      syncBar();
    });
    if (heroEl) io.observe(heroEl);
    if (footerEl) io.observe(footerEl);
  }

  // one cart write at a time; later writes wait for earlier ones and see fresh state
  let cartWriteChain = Promise.resolve();
  function queueCartWrite(fn) {
    cartWriteChain = cartWriteChain.then(fn, fn);
    return cartWriteChain;
  }

  function chip(id, item, source) {
    const el = document.createElement('div');
    el.className = 'kit-chip';
    el.innerHTML = `
      ${item.image ? `<img src="${esc(thumb(item.image, 88))}" alt="" width="44" height="44" loading="lazy">` : '<span class="kit-chip__ph"></span>'}
      <span class="kit-chip__meta">
        <span class="kit-chip__title">${esc(item.title)}</span>
        ${item.variantTitle ? `<span class="kit-chip__variant">${esc(item.variantTitle)}</span>` : ''}
        <span class="kit-chip__price">${money(item.price)}${item.qty > 1 ? ` &times; ${item.qty}` : ''}</span>
        ${source === 'cart' ? '<span class="kit-chip__incart">in your cart</span>' : ''}
      </span>
      <span class="kit-chip__qty">
        <button type="button" class="kit-chip__step" data-step="-1" aria-label="Fewer ${esc(item.title)}">&minus;</button>
        <b>${item.qty}</b>
        <button type="button" class="kit-chip__step" data-step="1" aria-label="More ${esc(item.title)}">&plus;</button>
      </span>
      <button type="button" class="kit-chip__remove" aria-label="Remove ${esc(item.title)}">&times;</button>`;

    el.querySelectorAll('.kit-chip__step').forEach((btn) => {
      btn.addEventListener('click', () => adjust(id, source, parseInt(btn.dataset.step, 10)));
    });
    el.querySelector('.kit-chip__remove').addEventListener('click', () => adjust(id, source, -item.qty));
    return el;
  }

  async function adjust(id, source, delta) {
    clearError();
    if (source === 'pending') {
      const item = pending.get(id);
      if (!item) return;
      const inCartQty = inCart.get(id) ? inCart.get(id).qty : 0;
      item.qty += delta;
      if (item.qty + inCartQty > MAX_PER) {
        item.qty = Math.max(MAX_PER - inCartQty, 0);
        showToast(`Max ${MAX_PER} per product.`);
      }
      if (item.qty <= 0) pending.delete(id);
      render();
    } else {
      // serialize cart writes: rapid +/- taps queue up instead of racing, and each
      // step re-reads the freshest quantity at execution time
      queueCartWrite(async () => {
        const item = inCart.get(id);
        if (!item) return;
        const pendingQty = pending.get(id) ? pending.get(id).qty : 0;
        let newQty = item.qty + delta;
        if (newQty + pendingQty > MAX_PER) {
          newQty = Math.max(MAX_PER - pendingQty, 0);
          showToast(`Max ${MAX_PER} per product.`);
        }
        if (newQty < 0) newQty = 0;
        if (newQty === item.qty) return;
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: item.key, quantity: newQty })
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            showError(err.description || err.message || 'Could not update your cart. Try again.');
          }
        } catch (e) {
          showError('Could not reach the cart. Check your connection and try again.');
        }
        await readCart();
        refreshDrawer();
      });
    }
  }

  function syncSubmitButtons() {
    if (submitting || Date.now() < flashUntil) return;
    const viewMode = pending.size === 0 && inCart.size > 0;
    const label = viewMode ? 'VIEW CART' : 'ADD MY KIT';
    const disabled = !viewMode && pending.size === 0;
    for (const btn of [submitBtn, reviewSubmit]) {
      if (!btn) continue;
      btn.textContent = label;
      btn.disabled = disabled;
      btn.dataset.mode = viewMode ? 'view' : 'add';
    }
  }

  // review internals rebuild only while the review step is what the shopper sees
  function renderReview() {
    const count = kitCount();
    const unlocked = count >= MIN;
    if (listEl) {
      listEl.querySelectorAll('.kit-chip').forEach((c) => c.remove());
      inCart.forEach((item, id) => listEl.appendChild(chip(id, item, 'cart')));
      pending.forEach((item, id) => listEl.appendChild(chip(id, item, 'pending')));
      if (emptyEl) emptyEl.hidden = count > 0;
    }
    if (reviewTotals) {
      const total = kitTotalCents();
      reviewTotals.hidden = count === 0;
      if (count > 0) {
        if (unlocked) {
          const disc = Math.round(total * (1 - PCT / 100));
          reviewTotals.innerHTML =
            `<span class="kit-review__row"><s>${money(total)}</s> <b>${money(disc)}</b></span>` +
            `<span class="kit-review__save">You're saving ${money(total - disc)}. Every item you add past ${MIN} gets ${PCT}% off too.</span>` +
            `<small>Estimated. Your cart shows the exact total.</small>`;
        } else {
          reviewTotals.innerHTML =
            `<span class="kit-review__row"><b>${money(total)}</b></span>` +
            `<span class="kit-review__save">${Math.max(MIN - count, 0)} more unlocks ${PCT}% OFF everything here.</span>`;
        }
      }
    }
  }

  function render() {
    const count = kitCount();
    const unlocked = count >= MIN;

    if (countEl) countEl.textContent = String(Math.min(count, 99));

    // on-card picked badges + ADD/stepper handover
    for (const c of cardIndex) {
      let qty = 0;
      for (const id of c.ids) qty += kitQtyFor(id);
      c.card.classList.toggle('is-picked', qty > 0);
      c.badge.hidden = qty === 0;
      if (qty > 0) c.badge.textContent = qty === 1 ? 'IN KIT' : `IN KIT ×${qty}`;
      if (c.stepper) {
        c.stepper.hidden = qty === 0;
        c.addBtn.hidden = qty > 0;
        c.stepperCount.textContent = String(qty);
      }
    }

    if (currentStep === 'review') renderReview();
    if (reviewHint) {
      reviewHint.textContent = unlocked
        ? `${PCT}% OFF unlocked. That's a Boss move.`
        : `${Math.max(MIN - count, 0)} more unlocks ${PCT}% OFF every item here.`;
    }

    if (bar) {
      if (barZero) barZero.hidden = count !== 0;
      if (barLocked) barLocked.hidden = unlocked || count === 0;
      if (barUnlocked) barUnlocked.hidden = !unlocked;
      if (barRemaining) barRemaining.textContent = String(Math.max(MIN - count, 0));
      const total = kitTotalCents();
      // the projected saving is REAL money computed from their actual picks - the
      // concrete number does the persuading that an abstract percent cannot
      if (barLocked && count > 0 && !unlocked) {
        const projected = Math.round(total * (PCT / 100));
        let proj = barLocked.querySelector('[data-kit-projected]');
        if (!proj) {
          proj = document.createElement('span');
          proj.setAttribute('data-kit-projected', '');
          proj.className = 'kit-bar__projected';
          barLocked.appendChild(proj);
        }
        proj.textContent = ` and get ${money(projected)} back on what you've picked`;
      }
      if (barPrice) barPrice.hidden = count === 0;
      if (total > 0 && unlocked) {
        if (barFull) { barFull.hidden = false; barFull.textContent = money(total); }
        if (barDisc) barDisc.textContent = money(Math.round(total * (1 - PCT / 100)));
      } else {
        if (barFull) barFull.hidden = true;
        if (barDisc) barDisc.textContent = money(total);
      }
      if (unlocked && !wasUnlocked && motion && barUnlocked) {
        motion.animate(barUnlocked, { opacity: [0, 1], transform: ['scale(0.92)', 'scale(1)'] }, { duration: 0.35 });
      }
      wasUnlocked = unlocked;
    }

    syncSubmitButtons();
    syncRail();
    syncStepNav();
    syncContinue();
    syncBar();
    persistPending();
  }

  /* ---------- submit: ONE atomic POST, retry once on throttle ---------- */
  async function submitKit(attempt) {
    // snapshot rebuilt per attempt so picks made during a retry wait are included
    const items = [];
    pending.forEach((p, id) => {
      items.push({ id, quantity: p.qty, properties: { [KIT_PROP]: '1' } });
    });
    let res;
    try {
      res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });
    } catch (e) {
      showError('Could not reach the cart. Check your connection and try again. Your picks are saved.');
      return null;
    }
    if (res.ok) return items;

    if (res.status === 429 && attempt === 0) {
      showToast('The store is busy right now. Retrying in a few seconds.');
      await new Promise((r) => setTimeout(r, 4000));
      return submitKit(1);
    }
    const raw = await res.text();
    let err = {};
    try { err = JSON.parse(raw); } catch { /* HTML error page */ }
    const detail = err.description || err.message;
    if (!detail) console.warn('kit submit failed', res.status, raw.slice(0, 200));
    showError(detail
      ? String(detail)
      : 'Could not add your kit. Nothing was charged and your picks are saved. Try again in a minute.');
    return null;
  }

  async function onSubmit() {
    if (submitBtn && submitBtn.dataset.mode === 'view') { refreshDrawer(); openDrawer(); return; }
    if (submitting || pending.size === 0) return;
    submitting = true;
    for (const btn of [submitBtn, reviewSubmit]) {
      if (btn) { btn.disabled = true; btn.textContent = 'ADDING…'; }
    }
    clearError();

    const posted = await submitKit(0);
    if (posted) {
      // subtract exactly what was posted: picks made while the POST was in flight survive
      for (const line of posted) {
        const p = pending.get(line.id);
        if (!p) continue;
        p.qty -= line.quantity;
        if (p.qty <= 0) pending.delete(line.id);
      }
      persistPending();
      await readCart();
      refreshDrawer();
      openDrawer();
      announce('Kit added to cart.');
      flashUntil = Date.now() + 1500;
      for (const btn of [submitBtn, reviewSubmit]) {
        if (btn) btn.textContent = 'KIT ADDED';
      }
      setTimeout(() => { render(); }, 1600);
    }
    submitting = false;
    render();
  }
  if (submitBtn) submitBtn.addEventListener('click', onSubmit);
  if (reviewSubmit) reviewSubmit.addEventListener('click', onSubmit);

  /* ---------- lifecycle ---------- */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      submitting = false;
      flashUntil = 0;
      readCart();
    }
  });

  restorePending();
  try {
    const savedStep = sessionStorage.getItem(STEP_KEY);
    if (savedStep && navIds.includes(savedStep) && savedStep !== currentStep) {
      steps.forEach((s) => { s.hidden = s.dataset.kitStep !== savedStep; });
      currentStep = savedStep;
    }
  } catch (e) { /* fine */ }
  syncRail();
  syncStepNav();
  readCart();
}
